export interface CompanyIdentifierRule {
  /** Property code in the properties system */
  propertyCode: string
  /** Display label key for i18n */
  labelKey: string
  /** Regex pattern for frontend format hint */
  formatHint?: string
  /** Example value shown as placeholder */
  placeholder?: string
  /**
   * Whether the identifier is mandatory for this legal form. Defaults to `true`
   * (country-driven identifiers for ordinary companies stay required). Set to
   * `false` for legal forms that may legitimately have no identifier — e.g. a
   * syndicat de copropriétaires without an RNC immatriculation.
   */
  required?: boolean
}

/**
 * Country-driven identifier for ordinary companies (`legalForm === 'company'`).
 * The legal form takes precedence over the country (see
 * {@link resolveCompanyIdentifierRule}); this map is the fallback for the
 * default `company` form.
 */
export const COMPANY_IDENTIFIER_BY_COUNTRY: Record<string, CompanyIdentifierRule> = {
  fr: {
    propertyCode: 'siren',
    labelKey: 'company.identifier.siren',
    formatHint: '\\d{9}',
    placeholder: '123 456 789',
    required: true,
  },
  gb: {
    propertyCode: 'crn',
    labelKey: 'company.identifier.crn',
    formatHint: '[A-Z0-9]{8}',
    placeholder: '12345678',
    required: true,
  },
  us: {
    propertyCode: 'ein',
    labelKey: 'company.identifier.ein',
    formatHint: '\\d{2}-\\d{7}',
    placeholder: '12-3456789',
    required: true,
  },
}

/**
 * Legal form of a personne morale. Drives which statutory identifier applies —
 * NOT the country. A syndicat de copropriétaires (loi 1965) is a personne morale
 * whose identifier is the RNC immatriculation number, not a SIREN, and often has
 * none at all.
 */
export type LegalForm =
  | 'company' // société commerciale / civile (SA, SARL, SAS, SCI…) — country-driven identifier
  | 'syndicat_coproprietaires' // syndicat des copropriétaires — RNC, optional
  | 'association' // association loi 1901 — RNA (n° W…), optional
  | 'asl_aful' // ASL / AFUL — SIRET or none, optional
  | 'other' // no statutory identifier

/** Ordered list of legal forms for select rendering. */
export const LEGAL_FORMS: LegalForm[] = [
  'company',
  'syndicat_coproprietaires',
  'association',
  'asl_aful',
  'other',
]

/**
 * Identifier rule for legal forms whose identifier is NOT country-driven.
 * `company` is intentionally absent — it resolves through
 * {@link COMPANY_IDENTIFIER_BY_COUNTRY}. `other` is absent — it carries no
 * identifier.
 */
export const COMPANY_IDENTIFIER_BY_LEGAL_FORM: Partial<Record<LegalForm, CompanyIdentifierRule>> = {
  syndicat_coproprietaires: {
    propertyCode: 'rnc_id',
    labelKey: 'company.identifier.rnc',
    placeholder: 'AA1234567',
    required: false,
  },
  association: {
    propertyCode: 'rna_id',
    labelKey: 'company.identifier.rna',
    formatHint: 'W\\d{9}',
    placeholder: 'W123456789',
    required: false,
  },
  asl_aful: {
    propertyCode: 'siret',
    labelKey: 'company.identifier.siret',
    formatHint: '\\d{14}',
    placeholder: '123 456 789 00012',
    required: false,
  },
}

/**
 * Resolve the applicable identifier rule for a personne morale from its legal
 * form (primary) and country (fallback for the default `company` form).
 * Returns `undefined` when the form carries no identifier (`other`) or when a
 * `company` has no recognised country.
 */
export function resolveCompanyIdentifierRule(
  legalForm: string | undefined,
  country: string | undefined,
): CompanyIdentifierRule | undefined {
  const lf = (legalForm || 'company') as LegalForm
  if (lf === 'other') return undefined
  if (lf !== 'company') return COMPANY_IDENTIFIER_BY_LEGAL_FORM[lf]
  const c = (country || '').toLowerCase()
  return c ? COMPANY_IDENTIFIER_BY_COUNTRY[c] : undefined
}

/** Whether an identifier rule mandates a value (defaults to required). */
export function isCompanyIdentifierRequired(rule: CompanyIdentifierRule | undefined): boolean {
  return !!rule && rule.required !== false
}
